import { useCallback, useEffect, useRef, useState } from 'react';
import {
  createSpeechRecognition,
  ensureMicrophoneAccess,
  isSpeechRecognitionSupported,
  micAccessErrorMessage,
} from '../utils/speechRecognition';

/**
 * Mic dictation via Web Speech API (SpeechRecognition).
 * Unlocks mic with getUserMedia first so browsers don't false-report "permission denied".
 */
export default function useSpeechRecognition({
  enabled = true,
  onFinalTranscript,
  onInterimTranscript,
  lang = 'en-US',
} = {}) {
  const supported = isSpeechRecognitionSupported();
  const [listening, setListening] = useState(false);
  const [error, setError] = useState(null);
  const recognitionRef = useRef(null);
  const intentionalStopRef = useRef(false);
  const startingRef = useRef(false);

  const stop = useCallback(() => {
    intentionalStopRef.current = true;
    startingRef.current = false;
    try {
      recognitionRef.current?.stop();
    } catch {
      /* ignore */
    }
    setListening(false);
  }, []);

  const start = useCallback(async () => {
    if (!supported || !enabled) {
      setError('Voice input is not supported in this browser. Try Chrome or Edge.');
      return;
    }
    if (startingRef.current || listening) return;

    startingRef.current = true;
    setError(null);
    intentionalStopRef.current = false;

    const access = await ensureMicrophoneAccess();
    if (!access.ok) {
      setError(micAccessErrorMessage(access.reason));
      startingRef.current = false;
      setListening(false);
      return;
    }

    // Stop any previous instance cleanly (avoid abort→not-allowed races)
    try {
      recognitionRef.current?.stop();
    } catch {
      /* ignore */
    }
    recognitionRef.current = null;

    const recognition = createSpeechRecognition({
      lang,
      continuous: true,
      interimResults: true,
    });
    if (!recognition) {
      setError('Voice input is not available.');
      startingRef.current = false;
      return;
    }

    recognition.onstart = () => {
      startingRef.current = false;
      setListening(true);
      setError(null);
    };

    recognition.onresult = (event) => {
      let interim = '';
      let finalText = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        const transcript = result?.[0]?.transcript || '';
        if (result.isFinal) finalText += transcript;
        else interim += transcript;
      }
      if (interim) onInterimTranscript?.(interim.trim());
      if (finalText.trim()) onFinalTranscript?.(finalText.trim());
    };

    recognition.onerror = (event) => {
      const code = String(event?.error || '');
      startingRef.current = false;

      // Benign / expected
      if (code === 'aborted' || code === 'no-speech') {
        setListening(false);
        return;
      }

      // If mic was already granted via getUserMedia, "not-allowed" is usually a
      // browser quirk — guide the user without claiming they blocked the mic.
      if (code === 'not-allowed') {
        setError(
          'Voice input was blocked by the browser. Click the lock icon next to the URL → Microphone → Allow, then reload this page.'
        );
      } else if (code === 'network') {
        setError('Speech service needs an internet connection. Check your network and try again.');
      } else if (code === 'service-not-allowed') {
        setError('Speech recognition is disabled in this browser. Try Chrome or Edge on desktop.');
      } else if (code === 'audio-capture') {
        setError(micAccessErrorMessage('busy'));
      } else {
        setError('Could not capture speech. Tap the mic and try again.');
      }
      setListening(false);
    };

    recognition.onend = () => {
      startingRef.current = false;
      setListening(false);
    };

    recognitionRef.current = recognition;

    // Tiny delay after getUserMedia release helps Chrome attach SpeechRecognition
    await new Promise((r) => setTimeout(r, 60));

    if (intentionalStopRef.current) {
      startingRef.current = false;
      return;
    }

    try {
      recognition.start();
    } catch (err) {
      startingRef.current = false;
      setListening(false);
      const msg = String(err?.message || err || '');
      if (/already started/i.test(msg)) {
        setListening(true);
        return;
      }
      setError('Could not start voice input. Tap the mic once and try again.');
    }
  }, [supported, enabled, lang, listening, onFinalTranscript, onInterimTranscript]);

  const toggle = useCallback(() => {
    if (listening || startingRef.current) stop();
    else start();
  }, [listening, start, stop]);

  useEffect(
    () => () => {
      intentionalStopRef.current = true;
      startingRef.current = false;
      try {
        recognitionRef.current?.stop();
      } catch {
        /* ignore */
      }
    },
    []
  );

  return {
    supported,
    listening,
    error,
    start,
    stop,
    toggle,
  };
}
